import { Badge, Button, Card, Container, Group, Image, Loader, ScrollArea, Stack, Text, Title } from "@mantine/core";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";

import nextCargoLogo from "@/assets/images/NextCargo.svg";
import { useGetCompaniesQuery } from "@/store/apis/nextcargo";
import { buildCompanyLoginUrl } from "@/utils/host";

const getCurrentSubdomain = () => {
  const hostname = window.location.hostname.toLowerCase();
  const hostnameWithoutPort = hostname.split(":")[0];
  const hostParts = hostnameWithoutPort.split(".").filter(Boolean);

  if (hostnameWithoutPort === "localhost" || hostParts.length <= 2) {
    return null;
  }

  return hostParts[0] || null;
};

export const Route = createFileRoute("/")({
  component: RouteComponent,
  beforeLoad: () => {
    const currentSubdomain = getCurrentSubdomain();

    if (!currentSubdomain || currentSubdomain === "dev") {
      throw redirect({ to: "/app/login" });
    }
  },
  head: () => ({
    meta: [
      {
        title: "NEXTCARGO"
      }
    ]
  })
});

function RouteComponent() {
  const { data: companies, isLoading, isFetching } = useGetCompaniesQuery();

  const activeCompanies = useMemo(() => (companies ?? []).filter((company) => company.status), [companies]);
  const shouldRedirect = activeCompanies.length === 1;
  const isEmpty = activeCompanies.length === 0;

  useEffect(() => {
    if (!shouldRedirect) return;

    window.location.replace(buildCompanyLoginUrl(activeCompanies[0]));
  }, [activeCompanies, shouldRedirect]);

  if (isLoading || isFetching || shouldRedirect) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-[radial-gradient(circle_at_top,rgba(252,143,0,0.18),transparent_34%),linear-gradient(180deg,#101820_0%,#081018_100%)] px-4 text-white">
        <Loader size="lg" color="orange" />
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-[radial-gradient(circle_at_top,rgba(252,143,0,0.16),transparent_28%),linear-gradient(180deg,#101820_0%,#081018_100%)] px-4 py-6 text-white sm:py-8 lg:py-10">
      <Container size="xl" className="flex min-h-[calc(100dvh-3rem)] items-start lg:min-h-[calc(100dvh-5rem)] lg:items-center">
        <div className="grid w-full gap-8 lg:grid-cols-[0.95fr_1.05fr] lg:items-center xl:gap-14">
          <Stack gap="sm">
            <Title order={1} className="text-balance" tt="uppercase" style={{ fontSize: "clamp(2.25rem, 6vw, 10rem)" }} lh={0.96}>
              Central <br /> de <br /> acesso
            </Title>
          </Stack>

          <Card id="empresas" radius="lg" p="lg" shadow="xl" className="w-full text-slate-900 backdrop-blur-md" withBorder>
            <Stack gap="lg">
              <Group justify="space-between" align="center">
                <Title order={2} c="dark" style={{ fontSize: "clamp(1.45rem, 3vw, 2.4rem)" }} lh={1.02}>
                  {!isEmpty ? "Acesse a unidade desejada" : "Nenhuma unidade ativa encontrada"}
                </Title>

                {!isEmpty && (
                  <div className="max-w-28">
                    <Image src={nextCargoLogo} alt="NextCargo" fit="contain" />
                  </div>
                )}
              </Group>

              <ScrollArea.Autosize mah={{ base: 260, sm: 340, lg: 520 }}>
                <Stack pb="sm">
                  {activeCompanies.map((company) => {
                    const loginUrl = buildCompanyLoginUrl(company);

                    return (
                      <Card key={company.id} radius="lg" p="md" shadow="sm" className="transition hover:border-orange-400/50 hover:shadow-md" withBorder>
                        <Group align="flex-start" justify="space-between" wrap="nowrap">
                          <Stack gap={2} className="min-w-0">
                            <Text fw={700} size="lg" c="dark" truncate>
                              {company.company || company.name}
                            </Text>
                            <Text size="sm" c="gray.7" truncate>
                              {company.city ? `${company.city} - ${company.uf}` : company.name}
                            </Text>
                          </Stack>

                          <Badge color="orange" variant="light" className="shrink-0">
                            Ativa
                          </Badge>
                        </Group>

                        <Group mt="md" justify="space-between" align="center">
                          <Text size="xs" c="gray.6" className="break-all">
                            {loginUrl}
                          </Text>

                          <Button component="a" href={loginUrl} color="orange" radius="xl" size="sm">
                            Entrar
                          </Button>
                        </Group>
                      </Card>
                    );
                  })}

                  {isEmpty && (
                    <Card radius="lg" p="lg" className="border-dashed! bg-slate-50" withBorder>
                      <Text c="gray.7">Nenhuma empresa ativa.</Text>
                    </Card>
                  )}
                </Stack>
              </ScrollArea.Autosize>
            </Stack>
          </Card>
        </div>
      </Container>
    </div>
  );
}
